import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Heart, ShoppingBag, Star, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  
  // Auto-select first variant when product changes
  useEffect(() => {
    if (product?.has_variants && product?.variants?.length > 0) {
      const firstActive = product.variants.find(v => v.is_active !== false && v.stock > 0);
      setSelectedVariant(firstActive || product.variants[0]);
    } else {
      setSelectedVariant(null);
    }
  }, [product]);
  
  if (!isOpen || !product) return null;

  const images = product.images?.length > 0 ? product.images : [product.image];
  const inWishlist = isInWishlist(product.id);

  // Get current price based on selected variant
  const getCurrentPrice = () => {
    if (product.has_variants && selectedVariant) {
      return selectedVariant.price;
    }
    return product.price;
  };

  const getOriginalPrice = () => {
    if (product.has_variants && selectedVariant) {
      return selectedVariant.original_price;
    }
    return product.originalPrice;
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = async () => {
    // Require size selection for variant products
    if (product.has_variants && !selectedVariant) {
      toast.error("Please select a size first");
      return;
    }
    
    try {
      if (product.has_variants && selectedVariant) {
        await addToCart(product.id, quantity, selectedVariant.id);
        const sizeName = selectedVariant.custom_label || selectedVariant.size_type;
        toast.success("Added to bag", { description: `${quantity}x ${product.name} (${sizeName}) added to your bag.` });
      } else {
        await addToCart(product.id, quantity);
        toast.success("Added to bag", { description: `${quantity}x ${product.name} has been added to your bag.` });
      }
      onClose();
    } catch (error) {
      toast.error("Error", { description: 'Failed to add to cart. Please try again.' });
    }
  };

  const handleToggleWishlist = async () => {
    try {
      await toggleWishlist(product.id);
      toast.success(inWishlist ? "Removed from wishlist" : "Added to wishlist");
    } catch (error) {
      toast.error("Error", { description: 'Failed to update wishlist.' });
    }
  };

  const isAddDisabled = () => {
    if (product.has_variants) {
      return !selectedVariant || (selectedVariant.stock <= 0);
    }
    return !product.in_stock;
  };

  const currentPrice = getCurrentPrice();
  const originalPrice = getOriginalPrice();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="quick-view-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
          data-testid="quick-view-close"
        >
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Image Section */}
          <div className="relative bg-gray-100 aspect-square md:aspect-auto md:h-[500px]">
            <img
              src={images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            
            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                >
                  <ChevronRight size={20} />
                </button>
                
                {/* Image dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-black' : 'bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col space-y-2">
              {product.new && <Badge className="bg-black text-white">New</Badge>}
              {product.bestseller && <Badge className="bg-white text-black">Bestseller</Badge>}
              {product.originalPrice && <Badge className="bg-red-600 text-white">Sale</Badge>}
            </div>
          </div>

          {/* Product Info Section */}
          <div className="p-6 md:p-8 flex flex-col">
            <div className="flex-1">
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">{product.brand}</p>
              <h2 className="text-2xl font-bold mb-2" data-testid="quick-view-title">{product.name}</h2>
              
              {/* Rating */}
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < Math.ceil(product.rating) ? 'fill-black text-black' : 'text-gray-300'}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  {product.rating?.toFixed(1)} ({product.reviewCount} reviews)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-2xl font-bold">£{currentPrice?.toFixed(2)}</span>
                {originalPrice && (
                  <span className="text-lg text-gray-500 line-through">
                    £{originalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-6 line-clamp-3">{product.description}</p>

              {/* Size Variants */}
              {product.has_variants && product.variants?.length > 0 ? (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Size *</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant) => {
                      const displayName = variant.custom_label || variant.size_type;
                      const isSelected = selectedVariant?.id === variant.id;
                      const isOutOfStock = variant.stock <= 0 || variant.is_active === false;
                      
                      return (
                        <button
                          key={variant.id}
                          onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                          disabled={isOutOfStock}
                          className={`px-3 py-1.5 border rounded-lg text-sm transition-all ${
                            isSelected 
                              ? 'border-black bg-black text-white' 
                              : isOutOfStock
                                ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 hover:border-black'
                          }`}
                        >
                          {displayName} - £{variant.price.toFixed(2)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : product.size && (
                <p className="text-sm text-gray-500 mb-4">Size: {product.size}</p>
              )}

              {/* Key Ingredients */}
              {product.ingredients?.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium mb-2">Key Ingredients:</p>
                  <div className="flex flex-wrap gap-2">
                    {product.ingredients.slice(0, 4).map((ingredient, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-sm rounded-full">
                        {ingredient}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="space-y-4 pt-4 border-t">
              {/* Quantity Selector */}
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium">Quantity:</span>
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 hover:bg-gray-100 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 hover:bg-gray-100 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex space-x-3">
                <Button 
                  onClick={handleAddToCart} 
                  className="flex-1 h-12"
                  disabled={isAddDisabled()}
                  data-testid="quick-view-add-to-cart"
                >
                  <ShoppingBag size={18} className="mr-2" />
                  {product.has_variants && !selectedVariant ? 'Select Size' : 'Add to Bag'}
                </Button>
                <Button 
                  onClick={handleToggleWishlist} 
                  variant="outline" 
                  className="h-12 px-4"
                  data-testid="quick-view-wishlist"
                >
                  <Heart 
                    size={18} 
                    className={inWishlist ? 'fill-red-500 text-red-500' : ''} 
                  />
                </Button>
              </div>

              {/* View Full Details Link */}
              <Link
                to={`/product/${product.id}`}
                onClick={onClose}
                className="block text-center text-sm text-gray-600 hover:text-black hover:underline"
              >
                View Full Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
